import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Flame, LogOut, ChevronLeft, WifiOff, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { isOnline, pendingCount, flushOutbox } from "@/lib/offline";
import { toast } from "sonner";

export const DriverShell = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();
  const isHome = loc.pathname === "/driver";
  const [online, setOnline] = useState(isOnline());
  const [pending, setPending] = useState(0);

  const refreshPending = useCallback(async () => {
    setPending(await pendingCount());
  }, []);

  const sync = useCallback(async () => {
    const res = await flushOutbox();
    await refreshPending();
    if (res.sent > 0) toast.success(`Synced ${res.sent} record(s)`);
  }, [refreshPending]);

  useEffect(() => {
    refreshPending();
    const goOnline = () => { setOnline(true); sync(); };
    const goOffline = () => setOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    const t = setInterval(refreshPending, 4000);
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
      clearInterval(t);
    };
  }, [refreshPending, sync]);

  if (!user) return null;
  return (
    <div className="min-h-screen bg-background flex flex-col max-w-md mx-auto border-x">
      <header className="sticky top-0 z-30 flex items-center justify-between border-b bg-card px-3 h-14">
        <div className="flex items-center gap-2">
          {isHome ? (
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary text-primary-foreground"><Flame className="h-5 w-5" /></div>
          ) : (
            <Button variant="ghost" size="icon" onClick={() => navigate("/driver")} data-testid="driver-back-button"><ChevronLeft className="h-5 w-5" /></Button>
          )}
          <div>
            <div className="font-head font-bold leading-none">Anila</div>
            <div className="text-[11px] text-muted-foreground">{user.name}</div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {pending > 0 && (
            <button onClick={sync} disabled={!online} data-testid="driver-sync-button"
              className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-950 border border-amber-200 px-2.5 py-1 text-xs font-medium">
              <RefreshCw className="h-3.5 w-3.5" /> {pending} pending
            </button>
          )}
          <Button variant="ghost" size="icon" onClick={logout} data-testid="driver-logout-button"><LogOut className="h-5 w-5" /></Button>
        </div>
      </header>
      {!online && (
        <div className="flex items-center gap-2 bg-amber-500 text-white text-sm px-4 py-2" data-testid="driver-offline-banner">
          <WifiOff className="h-4 w-4" /> Offline — saving on your phone. It will sync automatically.
        </div>
      )}
      <main className="flex-1 p-4 pb-24">{children}</main>
    </div>
  );
};
